import React, { useEffect, useState } from "react";
import { Menu, Bell, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/layout/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "AD";
}

export default function Topbar({ onMenu }) {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setCount(data.filter((n) => !n.read).length)).catch(() => {});
  }, []);

  const roleLabel = { firm_admin: "Firm Admin", advocate: "Advocate", staff: "Staff" }[user?.role] || "";

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-white/90 px-4 backdrop-blur-md sm:px-6">
      <button onClick={onMenu} className="lg:hidden" data-testid="topbar-menu-btn">
        <Menu className="h-6 w-6 text-navy" />
      </button>

      <div className="hidden flex-1 items-center md:flex">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="global-search-input"
            placeholder="Search cases, clients, courts…"
            onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) navigate(`/cases?q=${encodeURIComponent(e.target.value)}`); }}
            className="h-9 w-full rounded-md border border-border bg-secondary/50 pl-9 pr-3 text-sm outline-none transition-colors focus:border-navy focus:bg-white"
          />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="topbar-profile-btn" className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-secondary">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-navy text-xs text-white">{initials(user?.name)}</AvatarFallback>
              </Avatar>
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium leading-tight text-navy">{user?.name}</p>
                <p className="text-xs leading-tight text-muted-foreground">{roleLabel}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-medium">{user?.name}</p>
              <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/subscription")}>Subscription</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
